import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, ArrowUp, Zap } from "lucide-react";
import { ABtn, AScore, AResult, PadButton } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

const W = 320, H = 220, GROUND = 190, G = 0.7, JUMP = -11, SPD = 3;

export default function JungleCommando() {
  const canvasRef = useRef(null);
  const g = useRef({});
  const move = useRef(0);
  const jump = useRef(false);
  const scoreRef = useRef(0);
  const livesRef = useRef(3);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [status, setStatus] = useState("idle");

  const start = () => { g.current = { x: 30, y: GROUND - 22, vy: 0, onG: true, face: 1, bullets: [], enemies: [], spawn: 0 }; scoreRef.current = 0; setScore(0); livesRef.current = 3; setLives(3); setStatus("playing"); sfx.beep(); };
  const doJump = () => { const s = g.current; if (s && s.onG) { s.vy = JUMP; s.onG = false; } };
  const fire = () => { const s = g.current; if (!s.bullets) return; s.bullets.push({ x: s.x + (s.face > 0 ? 18 : -4), y: s.y + 8, vx: s.face * 7 }); sfx.shoot(); };

  useEffect(() => {
    const kd = (e) => { if (["ArrowLeft", "a"].includes(e.key)) { move.current = -1; e.preventDefault(); } else if (["ArrowRight", "d"].includes(e.key)) { move.current = 1; e.preventDefault(); } else if (["ArrowUp", "w"].includes(e.key)) { doJump(); e.preventDefault(); } else if (e.key === " ") { fire(); e.preventDefault(); } };
    const ku = (e) => { if (["ArrowLeft", "a", "ArrowRight", "d"].includes(e.key)) move.current = 0; };
    window.addEventListener("keydown", kd); window.addEventListener("keyup", ku);
    return () => { window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); };
  }, []);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    const draw = () => {
      ctx.fillStyle = "#1b3b2a"; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = "#14532d"; for (let i = 0; i < 6; i++) { ctx.fillRect(20 + i * 56, 60, 8, GROUND - 60); }
      ctx.fillStyle = "#3f2a12"; ctx.fillRect(0, GROUND, W, H - GROUND);
      ctx.fillStyle = "#5a3a1a"; ctx.fillRect(0, GROUND, W, 4);
      const s = g.current;
      ctx.fillStyle = "#f77737"; (s.bullets || []).forEach((b) => ctx.fillRect(b.x, b.y, 5, 3));
      (s.enemies || []).forEach((e) => { ctx.fillStyle = "#b91c1c"; ctx.fillRect(e.x, e.y, 14, 20); ctx.fillStyle = "#000"; ctx.fillRect(e.x + 3, e.y + 4, 3, 3); });
      ctx.fillStyle = "#2D6A4F"; ctx.fillRect(s.x || 0, s.y || 0, 14, 22); ctx.fillStyle = "#fcd9b6"; ctx.fillRect((s.x || 0) + 3, (s.y || 0) - 4, 8, 5);
      ctx.fillStyle = "#111"; ctx.fillRect((s.x || 0) + (s.face > 0 ? 12 : -6), (s.y || 0) + 7, 8, 3);
    };
    if (status !== "playing") { draw(); return; }
    let raf;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const s = g.current;
      if (jump.current) { doJump(); jump.current = false; }
      if (move.current) s.face = move.current;
      s.x = Math.max(0, Math.min(W - 14, s.x + move.current * SPD));
      s.vy += G; s.y += s.vy;
      if (s.y >= GROUND - 22) { s.y = GROUND - 22; s.vy = 0; s.onG = true; }
      s.bullets = s.bullets.filter((b) => { b.x += b.vx; return b.x > -10 && b.x < W + 10; });
      s.spawn--;
      if (s.spawn <= 0) { s.spawn = 70 + (Math.random() * 40 | 0); const fromRight = Math.random() > 0.35; s.enemies.push({ x: fromRight ? W + 6 : -20, y: GROUND - 20, vx: fromRight ? -(1 + Math.random() * 1.2) : (1 + Math.random() * 1.2) }); }
      for (const e of s.enemies) e.x += e.vx;
      // bullet hits
      for (const b of s.bullets) for (const e of s.enemies) { if (!e.dead && b.x > e.x && b.x < e.x + 14 && b.y > e.y && b.y < e.y + 20) { e.dead = true; b.x = 9999; scoreRef.current += 10; setScore(scoreRef.current); sfx.hit(); } }
      // enemy touches player
      let hit = false;
      s.enemies = s.enemies.filter((e) => { if (e.dead) return false; if (e.x < -30 || e.x > W + 30) return false; if (Math.abs(e.x - s.x) < 14 && Math.abs(e.y - s.y) < 22) { hit = true; return false; } return true; });
      if (hit) { livesRef.current -= 1; setLives(livesRef.current); sfx.explode(); if (livesRef.current <= 0) { cancelAnimationFrame(raf); setStatus("over"); return; } }
      draw();
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [status]);

  return (
    <div className="flex flex-col items-center">
      <div className="mb-2 flex gap-2"><AScore label="Score" value={score} /><AScore label="Lives" value={"❤".repeat(lives) || "0"} /></div>
      <div className="relative">
        <canvas ref={canvasRef} width={W} height={H} className="w-full max-w-[320px] rounded-md border-[3px] border-kirana-brown" style={{ imageRendering: "pixelated", touchAction: "none" }} />
        {status !== "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/60 p-3">
            {status === "over" && <AResult win={false} text={`Mission Over · ${score}`} testId="commando-over" />}
            <ABtn tone="green" onClick={start} className="mt-3">{status === "idle" ? "Start" : "Play Again"}</ABtn>
          </div>
        )}
      </div>
      <p className="mt-2 text-center text-xs text-kirana-cream/70">Move · Jump · Shoot the enemies</p>
      <div className="mt-1 flex items-center gap-2">
        <PadButton testId="commando-left" onPress={() => { move.current = -1; }} onRelease={() => { move.current = 0; }}><ArrowLeft className="h-5 w-5" /></PadButton>
        <PadButton testId="commando-right" onPress={() => { move.current = 1; }} onRelease={() => { move.current = 0; }}><ArrowRight className="h-5 w-5" /></PadButton>
        <PadButton testId="commando-jump" onPress={() => { jump.current = true; }}><ArrowUp className="h-5 w-5" /></PadButton>
        <PadButton testId="commando-fire" onPress={fire} className="w-14 bg-kirana-red text-white"><Zap className="h-5 w-5" /></PadButton>
      </div>
    </div>
  );
}
